import { Injectable } from "@angular/core";
import { FormGroup, FormRecord } from "@angular/forms";
import {
	Checklist,
	Result,
	ResultValue,
	Response,
	ImageAttachment,
	FormAnswerGroup,
	PageElement,
	ChecklistAssignmentResponse,
	AssignmentTrigger,
	Question,
	ManualReference,
	RideOpsManualVersion,
	VideoReference,
	LinkReference,
	RideOpsLinkVersion,
	RideOpsVideoVersion,
	DocumentationReference,
	RideOpsManualDocumentation,
} from "../checklist.interface";
import { ChecklistPageWithVisibility } from "../modals/checklist-modal.component";
import { firstValueFrom, map, timeout } from "rxjs";
import { LocationStateQuery } from "source/app/configuration/state/location-state.query";
import { HttpClient } from "@angular/common/http";
import { EnvironmentQuery } from "source/app/configuration/state/environment.query";
import { ChecklistStateService } from "../state/checklist-state.service";
import { validate as uuidValidate } from "uuid";
import { FileUploadsQuery } from "source/app/configuration/state/file-uploads.query";
import { FileTranslation } from "source/app/configuration/state/file-translation.model";
import { FileTranslationsQuery } from "source/app/configuration/state/file-translations.query";
import { ChecklistProgressStateService } from "../state/checklist-progress-state.service";
import { ElementType } from "../checklist.models";

@Injectable({ providedIn: "root" })
export class ChecklistService {
	constructor(
		private http: HttpClient,
		private environmentQuery: EnvironmentQuery,
		private locationStateQuery: LocationStateQuery,
		private checklistStateService: ChecklistStateService,
		private checklistProgressStateService: ChecklistProgressStateService,
		private fileTranslationsQuery: FileTranslationsQuery,
		private fileUploadsQuery: FileUploadsQuery,
	) {}

	submit = async (
		checklist: Checklist,
		checklistForm: FormRecord<FormGroup<FormAnswerGroup>>,
		visiblePages: ChecklistPageWithVisibility[],
	) => {
		if (checklistForm.valid) {
			const result = await this.convertToResult(checklist, checklistForm, visiblePages);
			const environment = await this.environmentQuery.environment$.firstAsync();

			const response = await this.http
				.post<ApiResponseWithData<string>>(`${environment.api.url}api/rideops/result`, result)
				.pipe(timeout(60000))
				.firstAsync();

			const resultId = response.data;

			if (checklist.activity === "Preopening") {
				this.checklistStateService.setPreopeningChecklistSubmitted(resultId);
			}

			this.checklistProgressStateService.clearProgress();
		}
	};

	getAssignmentTrigger = (elements: PageElement[], assignment: ChecklistAssignmentResponse): AssignmentTrigger => {
		if (assignment.fromTrigger === false) {
			return null;
		}

		const triggerCondition = assignment.origin.triggerCondition;
		const element = elements.find((element) => element.id === assignment.origin.question) as Question;

		if (triggerCondition == null || element == null) {
			return null;
		}

		const triggers = element.triggers.filter((x) => x.type === "AssignmentTrigger") as AssignmentTrigger[];

		return triggers.find(
			(x) =>
				x.condition.comparer === triggerCondition.comparer &&
				x.condition.left === triggerCondition.left &&
				x.condition.right === triggerCondition.right &&
				x.condition.type === triggerCondition.type,
		);
	};

	translateChecklist = (checklist: Checklist, language: string) => {
		const translateLocalized = (record: Record<string, string>): string => {
			if (record == null) return "";
			return record[language] ?? record[checklist.language];
		};

		checklist.pages.forEach((page) => {
			page.translatedName = translateLocalized(page.name);

			page.elements.forEach((element) => {
				if (element.stereotype === "Question") {
					const questionElement = element as Question;
					questionElement.translatedText = translateLocalized(questionElement.text);
					questionElement.translatedDescription = translateLocalized(questionElement.description);

					if (element.type === ElementType.SelectQuestion) {
						element.options.forEach((option) => {
							option.translatedText = translateLocalized(option.text);
						});
					}
				} else if (element.type === ElementType.TextElement) {
					element.translatedText = translateLocalized(element.text);
				} else if (element.stereotype === "Group" && element.type === "SelectQuestionGroup") {
					element.translatedText = translateLocalized(element.text);
					element.translatedDescription = translateLocalized(element.description);
				}
			});
		});
	};

	parseDocumentation = (checklist: Checklist) => {
		const elements = checklist.pages.flatMap((page) => page.elements);

		const mapDocumentation = <
			T extends RideOpsManualVersion | RideOpsVideoVersion | RideOpsLinkVersion,
			R extends DocumentationReference,
		>(
			items: string[] | { id: string; section: string }[],
			transform: (version: T, section?: DocumentReference) => Omit<R, keyof DocumentationReference>,
		): R[] => {
			return items
				.map((item) => {
					const documentation = checklist.documentation.find((document) =>
						typeof item === "string" ? document.id === item : document.id === item.id,
					);
					if (!documentation) return null;

					// Extract section
					const sectionId = typeof item !== "string" && "section" in item ? item.section : undefined;
					const section =
						sectionId == null
							? null
							: (documentation as RideOpsManualDocumentation).sections.find((x) => x.id === sectionId);

					return (documentation.versions as T[]).map((version) => ({
						id: version.id,
						name: documentation.name,
						version: version.name,
						...transform(version, section),
					}));
				})
				.filter((entry): entry is R[] => entry !== null) // Ensure we only keep valid results
				.flat();
		};

		elements.forEach((element) => {
			element.documentation = {
				manuals: mapDocumentation<RideOpsManualVersion, ManualReference>(
					element.manuals,
					(version, section) => ({
						file: section && version.sections[section.id] ? version.sections[section.id] : version.file, // Use section file if available, otherwise use version.file
						section: section?.name,
					}),
				),
				videos: mapDocumentation<RideOpsVideoVersion, VideoReference>(element.videos, (version) => ({
					code: version.code,
					type: version.type,
				})),
				links: mapDocumentation<RideOpsLinkVersion, LinkReference>(element.links, (version) => ({
					url: version.url,
				})),
			};
		});
	};

	private convertToResult = async (
		checklist: Checklist,
		checklistForm: FormRecord<FormGroup<FormAnswerGroup>>,
		pages: ChecklistPageWithVisibility[],
	): Promise<Result> => {
		let responses = this.getValuesFromForm(checklistForm);
		responses = await this.translateImages(responses);

		const pageVisibility = pages.map((page) => {
			return firstValueFrom(
				page.isVisible$.pipe(
					map((visible) => {
						return {
							visible: visible,
							id: page.id,
						};
					}),
				),
			);
		});

		const visiblePages = (await Promise.all(pageVisibility)).filter((x) => x.visible).map((x) => x.id);

		const elementVisibility = pages
			.flatMap((page) => page.elements)
			.map((element) => {
				return firstValueFrom(
					element.isVisible$.pipe(
						map((visible) => {
							return {
								visible$: visible,
								id: element.element.id,
							};
						}),
					),
				);
			});

		const visbleElements = (await Promise.all(elementVisibility)).filter((x) => x.visible$).map((x) => x.id);
		const location = await this.locationStateQuery.location$.firstAsync();

		return <Result>{
			answered: new Date().toISOString(),
			checklist: checklist.id,
			language: checklist.language,
			location: location.id,
			schedule: location.id,
			started: checklist.started,
			timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
			values: responses,

			scheduleUnit: {
				id: checklist.activity,
				origin: "RideOps",
			},

			visiblePages: visiblePages,
			visibleElements: visbleElements,

			metadata: {
				version: "RideOps",
				storageDriver: "N/A",
				deployVersion: "N/A",
			},
		};
	};

	public imagesThatCantbeTranslated = async (
		checklistForm: FormRecord<FormGroup<FormAnswerGroup>>,
	): Promise<string[]> => {
		const responses = this.getValuesFromForm(checklistForm);
		const imagesNotTranslated = this.imagesNotTranslated(responses);

		if (imagesNotTranslated.length === 0) {
			return [];
		}
		return this.fileUploadsQuery
			.selectMany(imagesNotTranslated)
			.pipe(map((fileUploads) => fileUploads.map((fu) => fu.id)))
			.firstAsync();
	};

	private translateImages = async (responses: Response[]): Promise<Response[]> => {
		const imagesNotTranslated = this.imagesNotTranslated(responses);
		if (imagesNotTranslated.length === 0) {
			return responses;
		}

		const translations = await this.fileTranslationsQuery.selectMany(imagesNotTranslated).firstAsync();

		const mapImageAttachment = (
			imageAttachment: ImageAttachment,
			translations: FileTranslation[],
		): ImageAttachment => {
			if (uuidValidate(imageAttachment.url)) {
				return {
					url: translations.find((translation) => translation.id === imageAttachment.url).path,
					cameraRoll: imageAttachment.cameraRoll,
				};
			}
			return imageAttachment;
		};

		const translatedResponses: Response[] = responses.map((response) =>
			uuidValidate(response.answers[0]) ||
			response.imageAttachments?.length > 0 ||
			response.assignments?.length > 0
				? {
						...response,
						answers: uuidValidate(response.answers[0])
							? [
									translations.find((translation) => translation.id === response.answers[0]).path,
									...response.answers.slice(1),
								]
							: response.answers,
						imageAttachments:
							response.imageAttachments == null || response.imageAttachments.length == 0
								? []
								: response.imageAttachments.map((image) => mapImageAttachment(image, translations)),
						assignments: response.assignments.map((assignment) => {
							const solution =
								assignment.solution == null
									? null
									: {
											...assignment.solution,
											attachments: assignment.solution.attachments.map((attachment) => {
												return attachment.type == undefined || attachment.type === "Image"
													? mapImageAttachment(attachment, translations)
													: attachment;
											}),
										};
							return {
								...assignment,
								solution: solution,
								attachments: assignment.attachments.map((attachment) => {
									return attachment.type == undefined || attachment.type === "Image"
										? mapImageAttachment(attachment, translations)
										: attachment;
								}),
							};
						}),
					}
				: response,
		);

		return translatedResponses;
	};

	private getValuesFromForm = (checklistForm: FormRecord<FormGroup<FormAnswerGroup>>) => {
		const values: ResultValue[] = Object.entries(checklistForm.value)
			.filter(([key, _value]) => checklistForm.controls[key].valid && checklistForm.controls[key].enabled) //we only want the valid answers
			.map(([key, value]) => {
				const retval: ResultValue = {
					answers: value.question.answers,
					answered: value.question.answered,
					position: value.question.position,
					user: value.question.user,
					question: key,
					comment: value.comment,
					imageAttachments: value.attachments,
					adHocScheduleSlots: value.question.adHocScheduleSlots,
					assignments: value.assignments.map((x) => {
						return {
							...x.assignment,
							solution: x.solution,
						};
					}),
				};
				return retval;
			});

		return values;
	};

	private imagesNotTranslated = (responses: Response[]): string[] => {
		return responses
			.map((response) =>
				response.imageAttachments?.length == 0 && response.assignments?.length == 0
					? [response.answers[0]]
					: [
							response.answers[0],
							...response.imageAttachments.map((ia) => ia.url),
							...response.assignments.flatMap((assignment) =>
								assignment.attachments
									.flatMap((attachment) => attachment.url)
									.concat(assignment.solution?.attachments.flatMap((attachment) => attachment.url)),
							),
						],
			)
			.flatMap((x) => x)
			.filter((x) => uuidValidate(x));
	};
}
